import type { StaticImageData } from "next/image";
import Image from "next/image";
import React from "react";
import { useHover, useLayer } from "react-laag";

import { InfoCircle } from "@/components/icons/info-circle";

type TooltipProps = {
  image: StaticImageData;
  heading: string;
  description: string;
};

export default function Tooltip({ image, heading, description }: TooltipProps) {
  const [isOver, hoverProps] = useHover();
  const [isFocused, setIsFocused] = React.useState(false);
  const tooltipId = React.useId();
  const isOpen = isOver || isFocused;

  const { triggerProps, layerProps, renderLayer } = useLayer({
    isOpen,
    auto: true,
    placement: "bottom-start",
    triggerOffset: 4,
    overflowContainer: false,
  });

  return (
    <>
      <button
        type="button"
        {...triggerProps}
        {...hoverProps}
        data-testid="tooltip-trigger-icon"
        aria-label={heading}
        aria-describedby={isOpen ? tooltipId : undefined}
        className="rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setIsFocused(false);
          }
        }}
      >
        <InfoCircle className="size-3.5 text-gray-600" aria-hidden="true" />
      </button>
      {isOpen &&
        renderLayer(
          <div {...layerProps} className="z-50 rounded shadow-md">
            <div
              id={tooltipId}
              role="tooltip"
              className="w-56 rounded bg-white p-2"
              data-testid="tooltip-content"
            >
              <p className="mb-1 text-xs font-semibold text-gray-900">
                {heading}
              </p>
              <p className="mb-1 text-xs text-gray-800">{description}</p>
              <Image
                alt=""
                objectFit="contain"
                src={image}
                height={94}
                width={208}
                loading="lazy"
                unoptimized
                className="rounded"
              />
            </div>
          </div>
        )}
    </>
  );
}
